import {
  getSafeLocalStorage,
  type SafeStorageErrorHandler,
} from '@heidi/utils/jotai-storage';

import {
  emitStorageEvent,
  type VersionedStorageEvent,
  type VersionedStorageEventHandler,
} from './telemetry';

/**
 * Versioned persisted storage (arch §9.2 / Wave 1 · G2, ARCFE-658).
 *
 * The monorepo migration moves persisted-state owners (Rematch → jotai,
 * app → package). Without explicit versioning, a shape or key change silently
 * loses user state on upgrade — worst on desktop, where installs are
 * long-lived and staged rollouts make old and new binaries share the same
 * WebView storage. This module is the §9.2 convention, mechanized
 * (`docs/monorepo-rematch-removal.md` §2.2–2.3):
 *
 * - `defineVersionedStorage` wraps a persisted value in a `{ v, data }`
 *   envelope with an ordered, cumulative migration chain and a fallback. It
 *   never throws: missing, corrupt, foreign and future-version data all
 *   resolve the fallback and report an event, and a write the browser
 *   rejects is reported rather than raised. "Corrupt" is a judgement about
 *   the ENVELOPE, not its payload — a well-formed envelope whose `data` is
 *   the wrong shape is accepted unless the key supplies the optional
 *   `validate` guard.
 * - `runStorageMigrations` is the boot-time runner for CROSS-KEY moves
 *   (e.g. seeding new atoms from redux-persist's `persist:root` blob). It
 *   runs before hydration, is idempotent, and is non-destructive by default
 *   (legacy keys survive one release window — §2.3.4).
 *
 * The laws this encodes (§2.3): storage keys are FROZEN contracts (explicit
 * strings, never derived, never renamed in a move — new state gets namespaced
 * keys like `heidi.workspace.prefs.v1`); every PR that moves or reshapes
 * persisted state ships its migration + fallback + a captured-fixture test in
 * the same PR; enforcement is the persistence-manifest diff in CI, not habit.
 *
 * Telemetry is an injected callback (`onEvent`) — packages never read env or
 * import loggers (variant-agnostic rule, onprem-guardrails F5); the app wires
 * the callback to Datadog so silent data loss surfaces in dashboards, counts
 * and versions only (PHI-safe). Because this code runs long before app
 * bootstrap, events are held by the pre-wiring buffer in ./telemetry and
 * flushed when the app declares its wiring complete.
 *
 * Home: `@heidi/runtime/storage` — the door `docs/monorepo-rematch-removal.md`
 * §2.2 names as the utility's owner. The safe-localStorage primitive stays in
 * `@heidi/utils/jotai-storage` (formerly `@heidi/state`); this module owns the
 * versioned envelope + cross-key migrations.
 */

/**
 * `migrations[n]` upgrades the version-(n-1) payload to version n. The chain
 * is cumulative and idempotent by construction (pure functions, run in order)
 * so a v1 → v5 jump (sporadic desktop updates) replays 2, 3, 4, 5. Chains are
 * kept indefinitely.
 */
export interface VersionedStorageConfig<T> {
  /** FROZEN storage key (§2.3.1) — explicit, namespaced (`heidi.<domain>.<name>.v1`), never renamed. */
  key: string;
  /** Current shape version, ≥ 1. Bumping it REQUIRES adding `migrations[newVersion]`. */
  version: number;
  /** Step upgrades keyed by TARGET version: `2: (v1Data) => v2Data`. */
  migrations?: Partial<Record<number, (previous: unknown) => unknown>>;
  /** Returned whenever the stored value is missing, corrupt, foreign, or from the future. */
  fallback: T;
  /**
   * Optional payload check for a CURRENT-version envelope, applied after the
   * migration chain has run. Defaults to accepting anything, which is the
   * pre-existing behaviour for every key that does not opt in.
   *
   * The realistic bad-payload vector is not hand-edited storage: it is a
   * cross-key legacy transform copying a redux value through verbatim, so the
   * envelope is well-formed and the version is right while `data` is whatever
   * the old slice held. Without a guard that value is handed to callers as
   * `T` — which is how a `widgetToken` object becomes `Bearer [object
   * Object]` on the wire.
   */
  validate?: (data: unknown) => data is T;
  /** Injected telemetry (app wires Datadog); packages never log/read env themselves. */
  onEvent?: VersionedStorageEventHandler;
}

interface Envelope {
  v: number;
  data: unknown;
}

function isEnvelope(value: unknown): value is Envelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    // INTEGER, not just `number`: a fractional or infinite `v` compares
    // `!== version` and `> version` falsely, so `{"v":0.5}` would skip the
    // migration loop entirely and then be written BACK as a current-version
    // envelope — unmigrated data laundered into the current shape.
    Number.isInteger((value as { v?: unknown }).v) &&
    'data' in value
  );
}

/**
 * Decode + migrate a raw stored string to the current version.
 * Returns `undefined` when the caller should use the fallback; never throws.
 * Pure aside from the event callback — storage writes are the caller's call.
 */
function decodeToCurrent<T>(
  raw: string | null,
  config: VersionedStorageConfig<T>
): { value: T; migratedFrom?: number } | undefined {
  if (raw === null) return undefined;

  const fallbackTo = (
    reason: Extract<VersionedStorageEvent, { type: 'fallback' }>['reason']
  ): undefined => {
    emitStorageEvent(config.onEvent, { type: 'fallback', key: config.key, reason });
    return undefined;
  };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallbackTo('corrupt');
  }
  if (!isEnvelope(parsed)) {
    return fallbackTo('corrupt');
  }

  const accept = (
    data: unknown,
    migratedFrom?: number
  ): { value: T; migratedFrom?: number } | undefined => {
    if (config.validate !== undefined && !config.validate(data)) {
      return fallbackTo('invalid-shape');
    }
    return { value: data as T, migratedFrom };
  };

  if (parsed.v === config.version) {
    return accept(parsed.data);
  }

  // Downgrade path: a NEWER envelope read by an OLDER binary (desktop staged
  // rollouts share WebView storage — §2.3.4). Non-destructive: fall back in
  // memory, never overwrite the newer data.
  if (parsed.v > config.version) {
    return fallbackTo('future-version');
  }

  let data: unknown = parsed.data;
  for (let target = parsed.v + 1; target <= config.version; target++) {
    const step = config.migrations?.[target];
    if (!step) {
      return fallbackTo('migration-failed');
    }
    try {
      data = step(data);
    } catch {
      return fallbackTo('migration-failed');
    }
  }
  const migrated = accept(data, parsed.v);
  if (migrated === undefined) return undefined;
  emitStorageEvent(config.onEvent, {
    type: 'migrated',
    key: config.key,
    from: parsed.v,
    to: config.version,
  });
  return migrated;
}

function encode(version: number, data: unknown): string {
  return JSON.stringify({ v: version, data });
}

/** The storage shape jotai's `atomWithStorage` accepts as its third argument. */
export interface VersionedSyncStorage<T> {
  getItem: (key: string, initialValue: T) => T;
  setItem: (key: string, value: T) => void;
  removeItem: (key: string) => void;
}

export interface VersionedStorage<T> {
  /**
   * The envelope-aware storage adapter — THE door for atom consumers, who
   * pass it as jotai's third `atomWithStorage` argument and keep ownership of
   * the fourth (the options).
   *
   * Pass `getOnInit: true` unless nothing reads the value before mount. It is
   * not optional dressing: without it the atom resolves its initial value
   * until first mounted, so a `getDefaultStore().get(...)` on a boot path
   * reads the fallback — which for a credential means sending an
   * unauthenticated request.
   */
  adapter: VersionedSyncStorage<T>;
  /** One-shot read (+ migrate + write-back). For non-atom consumers and boot code. */
  read: () => T;
  /** One-shot envelope write. */
  write: (value: T) => void;
  /** Remove the key (tests / explicit resets — production deletes follow §2.3.4). */
  clear: () => void;
}

/**
 * Define a versioned persisted value. Reads decode `{ v, data }`, replay the
 * migration chain when behind, and fall back (without throwing) on anything
 * unreadable; successful migrations write back so storage converges forward.
 *
 * This returns no ready-made atom on purpose. It used to, built without
 * `getOnInit`, and every production key ignored it and composed its own —
 * because the built-in one read the fallback before mount, and because a
 * wrapper around the adapter (undefined-as-absent, user scoping) has to sit
 * between the envelope and the atom anyway. `adapter` is the composition
 * point; the dead constructor was a door that looked primary and was wrong.
 */
export function defineVersionedStorage<T>(
  config: VersionedStorageConfig<T>
): VersionedStorage<T> {
  if (config.version < 1) {
    throw new Error(`[versioned-storage] "${config.key}": version must be >= 1`);
  }

  // Quota exhaustion and private-mode denial are the failures a persisted
  // value cannot detect for itself: the safe wrapper swallows the throw and
  // the next read simply returns the old value. Reads that throw already
  // resolve to the fallback and report through the decode path, so only
  // writes need surfacing here.
  const reportStorageFailure: SafeStorageErrorHandler = (operation, key) => {
    if (operation !== 'set') return;
    emitStorageEvent(config.onEvent, { type: 'write-failed', key });
  };
  const storage = (): ReturnType<typeof getSafeLocalStorage> =>
    getSafeLocalStorage(reportStorageFailure);

  const readAndConverge = (): T => {
    const decoded = decodeToCurrent(storage().getItem(config.key), config);
    if (decoded === undefined) return config.fallback;
    if (decoded.migratedFrom !== undefined) {
      storage().setItem(config.key, encode(config.version, decoded.value));
    }
    return decoded.value;
  };

  const write = (value: T): void => {
    storage().setItem(config.key, encode(config.version, value));
  };

  const storageAdapter: VersionedSyncStorage<T> = {
    getItem: (_key: string, initialValue: T): T => {
      // Delegates to the same decode+converge path; initialValue mirrors the
      // configured fallback (jotai passes the atom's initial value through).
      const decoded = decodeToCurrent(storage().getItem(config.key), config);
      if (decoded === undefined) return initialValue;
      if (decoded.migratedFrom !== undefined) {
        storage().setItem(config.key, encode(config.version, decoded.value));
      }
      return decoded.value;
    },
    setItem: (_key: string, value: T): void => write(value),
    // jotai passes back the key the atom was constructed with (=== config.key),
    // so this is the same key `write` and `clear` use. It is threaded through
    // rather than closed over only because jotai owns the argument; a caller
    // handing it a different key would remove that other key.
    removeItem: (key: string): void => storage().removeItem(key),
  };

  return {
    adapter: storageAdapter,
    read: readAndConverge,
    write,
    clear: () => storage().removeItem(config.key),
  };
}

/**
 * A cross-key, cross-paradigm move: read a LEGACY key (e.g. redux-persist's
 * `persist:root`, which is stringified JSON of stringified slices), transform
 * it, seed the TARGET key. The runner never routes through the legacy
 * framework — it reads the serialized blob directly (§2.2).
 */
export interface StorageMigration {
  /** Unique id — tombstone key + telemetry label (e.g. `auth-to-workspace.v1`). */
  id: string;
  /** The legacy storage key to read (left in place by default — §2.3.4). */
  legacyKey: string;
  /** Target key to seed. The migration is skipped when it already has a value. */
  targetKey: string;
  /**
   * Transform the RAW legacy string into the target's stored string
   * (including its `{ v, data }` envelope — use `encodeVersioned`).
   * Return `null` to skip seeding (e.g. legacy blob has nothing usable) —
   * that run reports `legacy-skipped`, not `legacy-migrated`, because a
   * migration that seeded nothing is the shape total data loss takes.
   * Throwing marks the run failed (tombstoned as attempted; the target key's
   * own fallback rules then apply at read time).
   */
  transform: (rawLegacy: string) => string | null;
  /**
   * Delete the legacy key after seeding. DEFAULT FALSE — legacy keys survive
   * one release window (desktop fleet-minimum-version rule, §2.3.4); flip
   * this only in the follow-up cleanup release.
   */
  cleanupLegacy?: boolean;
}

const TOMBSTONE_PREFIX = 'heidi.storage-migration.';

/** Envelope helper for `StorageMigration.transform` implementations. */
export function encodeVersioned(version: number, data: unknown): string {
  return encode(version, data);
}

/**
 * Boot-time migration runner — call ONCE at app boot, BEFORE store/atom
 * hydration (§2.2). Idempotent: a migration runs at most once per browser
 * profile (tombstoned), and never overwrites an existing target value.
 */
export function runStorageMigrations(
  migrations: readonly StorageMigration[],
  onEvent?: VersionedStorageEventHandler
): void {
  const storage = getSafeLocalStorage((operation, key) => {
    if (operation !== 'set') return;
    emitStorageEvent(onEvent, { type: 'write-failed', key });
  });
  for (const migration of migrations) {
    const tombstoneKey = `${TOMBSTONE_PREFIX}${migration.id}`;
    if (storage.getItem(tombstoneKey) !== null) continue;
    if (storage.getItem(migration.targetKey) !== null) {
      // Target already live (fresh install, or seeded by a newer binary) —
      // record and never touch it again.
      storage.setItem(tombstoneKey, '1');
      continue;
    }

    const rawLegacy = storage.getItem(migration.legacyKey);
    if (rawLegacy === null) {
      // Nothing to migrate (fresh profile). Tombstone so we don't re-probe.
      storage.setItem(tombstoneKey, '1');
      continue;
    }

    try {
      const seeded = migration.transform(rawLegacy);
      if (seeded !== null) {
        storage.setItem(migration.targetKey, seeded);
      }
      if (migration.cleanupLegacy) {
        storage.removeItem(migration.legacyKey);
      }
      storage.setItem(tombstoneKey, '1');
      // A run that seeded nothing is reported as its own thing. The transform
      // returning null is the ordinary "this field was never in the blob"
      // case per key, but it is ALSO what a blob whose slice has been
      // rewritten away looks like — and that is total loss for the key,
      // tombstoned so it can never be retried. One event type for both would
      // make every key report success while every key stayed empty.
      emitStorageEvent(onEvent, {
        type: seeded === null ? 'legacy-skipped' : 'legacy-migrated',
        id: migration.id,
        legacyKey: migration.legacyKey,
        targetKey: migration.targetKey,
      });
    } catch {
      // Failed transforms tombstone too: a deterministic transform that threw
      // once will throw every boot — retry loops just delay fallback. The
      // event is the signal; the target's fallback keeps the app usable.
      storage.setItem(tombstoneKey, '1');
      emitStorageEvent(onEvent, {
        type: 'legacy-migration-failed',
        id: migration.id,
        legacyKey: migration.legacyKey,
      });
    }
  }
}
